import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mobileWidth } from '@/constants';
import {
  AccountCategory,
  BalanceDataInterface,
} from '@/interfaces/balanceSheet';
import {
  Box,
  useMediaQuery,
  Button,
  Typography,
  Paper,
  Divider,
} from '@mui/material';
import { ToggleDataView } from '@/components/common/ToggleDataView';
import { BalanceChartLayout } from '@/modules/balanceSheet/components/BalanceChartLayout';
import { DataRangeFilterEnum } from '@/constants/dates';
import { useDrilldown } from '@/hooks/useDrilldown';
import { DrilldownView } from '@/components/views/DrilldownView';
import AccountsGrid from '@/components/grids/AccountsGrid';
import { useGraphColors } from '@/hooks/useGraphColors';
import {
  HorizontalBarChart,
  LegendToggleButton,
  ValueLabelsToggleButton,
  type HorizontalBarDatum,
  type LegendDisplayMode,
} from '@/components/charts/HorizontalBarChart';
import { formatPercentValue } from '@/utils/numbers';
import { formatNumberToCurrency } from '@/utils/currency';
import { useWidgetWidthIsBelow } from '@/hooks/useWidgetWidthIsBelow';

/** Whether each side of the chart shows percentages (true) or dollar values. */
export interface ChartPercentModes {
  assets: boolean;
  liabilities: boolean;
}

interface CategoryBarChartProps {
  balanceData: BalanceDataInterface;
  dateFilter: DataRangeFilterEnum;
  enableDrilldown?: boolean;
  isWidget?: boolean;
  /**
   * Stacks assets above liabilities. Left undefined, the chart falls back to
   * stacking in any widget, which is what callers relied on before IU-245.
   */
  stacked?: boolean;
  /** When provided, a bar click bubbles up instead of drilling inline (IU-31). */
  onDrilldown?: (category: AccountCategory) => void;
  /** Drill-down accounts grid fills the page height. Off inside a widget. */
  fillDrilldownHeight?: boolean;
  /**
   * Groups the drilled-down accounts by subcategory (EUD-886). Set by the
   * balance sheet, whose bars are classifications; left off where the bars are
   * institutions, data sources or tags.
   */
  groupBySubcategory?: boolean;
  /**
   * Controls the $/% choice from outside, for a host that unmounts the chart
   * while it reloads and needs the choice to survive (IU-245). Left undefined,
   * the chart keeps the choice itself.
   */
  percentModes?: ChartPercentModes;
  onPercentModesChange?: (modes: ChartPercentModes) => void;
}

const MIN_CHART_HEIGHT = 600;
const ROW_HEIGHT = 28;
const AXIS_SPACE = 60;
const MAX_LABEL_SHARE = 0.4;

/**
 * Height that gives every category its own row, so a long list doesn't squeeze
 * its labels on top of each other. A short list keeps the usual height.
 */
const chartHeightFor = (categoryCount: number): number =>
  Math.max(MIN_CHART_HEIGHT, categoryCount * ROW_HEIGHT + AXIS_SPACE);

const renderCategoryTooltip = (datum: HorizontalBarDatum) => {
  return (
    <Paper
      elevation={3}
      sx={{ p: 1, pointerEvents: 'none' }}
    >
      <Typography
        variant='body2'
        sx={{ textAlign: 'left', fontWeight: 'bold' }}
      >
        {datum.name}
      </Typography>
      <Divider sx={{ my: 0.5 }} />
      <Typography
        variant='body2'
        sx={{ textAlign: 'left' }}
      >
        {formatNumberToCurrency(datum.rawValue)}
      </Typography>
      <Typography
        variant='body2'
        sx={{ textAlign: 'left' }}
      >
        {formatPercentValue(datum.percentValue)}
      </Typography>
    </Paper>
  );
};

export default function CategoryBarChart({
  balanceData,
  enableDrilldown = true,
  isWidget = false,
  stacked,
  onDrilldown,
  groupBySubcategory = false,
  fillDrilldownHeight = false,
  percentModes,
  onPercentModesChange,
}: CategoryBarChartProps) {
  const { t } = useTranslation();
  const { assets, liabilities } = balanceData;
  const isMobileMatch = useMediaQuery(mobileWidth);
  const graphColors = useGraphColors();
  const { drilledDownCategory, handleDrilldown, handleBackClick } =
    useDrilldown();

  const [ownPercentModes, setOwnPercentModes] = useState<ChartPercentModes>({
    assets: true,
    liabilities: true,
  });
  const currentPercentModes = percentModes ?? ownPercentModes;
  const isAssetsPercent = currentPercentModes.assets;
  const isLiabilitiesPercent = currentPercentModes.liabilities;
  const changePercentModes = (next: ChartPercentModes) => {
    if (onPercentModesChange) onPercentModesChange(next);
    if (!percentModes) setOwnPercentModes(next);
  };
  const setIsAssetsPercent = (assets: boolean) => {
    changePercentModes({ ...currentPercentModes, assets });
  };
  const setIsLiabilitiesPercent = (liabilities: boolean) => {
    changePercentModes({ ...currentPercentModes, liabilities });
  };
  const [assetsLegendDisplay, setAssetsLegendDisplay] =
    useState<LegendDisplayMode>('axis');
  const [liabilitiesLegendDisplay, setLiabilitiesLegendDisplay] =
    useState<LegendDisplayMode>('axis');
  const [showAssetsValues, setShowAssetsValues] = useState(false);
  const [showLiabilitiesValues, setShowLiabilitiesValues] = useState(false);

  const toggleAssetsLegend = () => {
    setAssetsLegendDisplay((m) => (m === 'axis' ? 'legend' : 'axis'));
  };
  const toggleLiabilitiesLegend = () => {
    setLiabilitiesLegendDisplay((m) => (m === 'axis' ? 'legend' : 'axis'));
  };
  const toggleAssetsValues = () => {
    setShowAssetsValues((v) => !v);
  };
  const toggleLiabilitiesValues = () => {
    setShowLiabilitiesValues((v) => !v);
  };

  const assetsData: HorizontalBarDatum[] = useMemo(
    () =>
      [...assets.categories]
        .sort((a, b) => b.total / assets.total - a.total / assets.total)
        .map((category, index) => ({
          id: index,
          name: category.name,
          rawValue: category.total,
          percentValue: (category.total / assets.total) * 100,
        })),
    [assets],
  );

  const liabilitiesData: HorizontalBarDatum[] = useMemo(
    () =>
      [...liabilities.categories]
        .sort(
          (a, b) => b.total / liabilities.total - a.total / liabilities.total,
        )
        .map((category, index) => ({
          id: index,
          name: category.name,
          rawValue: category.total,
          percentValue: (category.total / liabilities.total) * 100,
        })),
    [liabilities],
  );

  const triggerDrilldown = onDrilldown ?? handleDrilldown;

  const handleAssetsClick = (datum: HorizontalBarDatum) => {
    if (!enableDrilldown) return;
    const category = assets.categories.find((c) => c.name === datum.name);
    if (category) triggerDrilldown(category);
  };

  const handleLiabilitiesClick = (datum: HorizontalBarDatum) => {
    if (!enableDrilldown) return;
    const category = liabilities.categories.find((c) => c.name === datum.name);
    if (category) triggerDrilldown(category);
  };

  const stackCharts = stacked ?? isWidget;
  const { ref: chartAreaRef, widthPx: chartAreaWidth } = useWidgetWidthIsBelow(
    true,
    0,
  );
  // A label column sized for a full page leaves almost nothing for the bars in a
  // narrow card, so it never takes more than its share of one chart's width.
  const chartWidth =
    chartAreaWidth !== null && !stackCharts && !isMobileMatch
      ? chartAreaWidth / 2
      : chartAreaWidth;
  const baseLabelWidth = isMobileMatch ? 150 : 180;
  const labelWidth =
    chartWidth === null
      ? baseLabelWidth
      : Math.min(baseLabelWidth, Math.floor(chartWidth * MAX_LABEL_SHARE));

  const AssetsBarChart = (
    <Box
      flex={1}
      minWidth={0}
      position='relative'
      sx={{
        paddingTop: 4,
        px: { xs: 0, lg: 0 },
        '@media print': {
          paddingTop: 0,
          marginTop: 0,
        },
      }}
      className='chart-print-safe'
    >
      <div className='print-hide'>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 3,
            display: 'flex',
          }}
        >
          <LegendToggleButton
            mode={assetsLegendDisplay}
            onToggle={toggleAssetsLegend}
          />
          <ValueLabelsToggleButton
            visible={showAssetsValues}
            onToggle={toggleAssetsValues}
          />
        </Box>
        <ToggleDataView
          isPercent={isAssetsPercent}
          setIsPercent={setIsAssetsPercent}
        />
      </div>
      <HorizontalBarChart
        data={assetsData}
        valueMode={isAssetsPercent ? 'percent' : 'currency'}
        legendDisplay={assetsLegendDisplay}
        onLegendDisplayChange={setAssetsLegendDisplay}
        showValueLabels={showAssetsValues}
        valueLabelFormatter={(datum) =>
          isAssetsPercent
            ? formatPercentValue(datum.percentValue)
            : formatNumberToCurrency(datum.rawValue)
        }
        height={chartHeightFor(assetsData.length)}
        yAxisLabelWidth={labelWidth}
        colors={graphColors}
        xAxisLabel={
          isAssetsPercent
            ? t('CategoryBarChart.percentage')
            : t('CategoryBarChart.amount')
        }
        onBarClick={enableDrilldown ? handleAssetsClick : undefined}
        tooltipRenderer={renderCategoryTooltip}
      />
    </Box>
  );

  const LiabilitiesBarChart = (
    <Box
      flex={1}
      minWidth={0}
      position='relative'
      sx={{
        paddingTop: 4,
        px: { xs: 0, lg: 0 },
        '@media print': {
          paddingTop: 0,
          marginTop: 0,
        },
      }}
      className='chart-print-safe'
    >
      <div className='print-hide'>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            zIndex: 3,
            display: 'flex',
          }}
        >
          <LegendToggleButton
            mode={liabilitiesLegendDisplay}
            onToggle={toggleLiabilitiesLegend}
          />
          <ValueLabelsToggleButton
            visible={showLiabilitiesValues}
            onToggle={toggleLiabilitiesValues}
          />
        </Box>
        <ToggleDataView
          isPercent={isLiabilitiesPercent}
          setIsPercent={setIsLiabilitiesPercent}
        />
      </div>
      <HorizontalBarChart
        data={liabilitiesData}
        valueMode={isLiabilitiesPercent ? 'percent' : 'currency'}
        legendDisplay={liabilitiesLegendDisplay}
        onLegendDisplayChange={setLiabilitiesLegendDisplay}
        showValueLabels={showLiabilitiesValues}
        valueLabelFormatter={(datum) =>
          isLiabilitiesPercent
            ? formatPercentValue(datum.percentValue)
            : formatNumberToCurrency(datum.rawValue)
        }
        height={chartHeightFor(liabilitiesData.length)}
        yAxisLabelWidth={labelWidth}
        colors={graphColors}
        xAxisLabel={
          isLiabilitiesPercent
            ? t('CategoryBarChart.percentage')
            : t('CategoryBarChart.amount')
        }
        onBarClick={enableDrilldown ? handleLiabilitiesClick : undefined}
        tooltipRenderer={renderCategoryTooltip}
      />
    </Box>
  );

  if (enableDrilldown && drilledDownCategory) {
    return (
      <DrilldownView
        category={drilledDownCategory}
        fillHeight={fillDrilldownHeight}
        onBack={handleBackClick}
        groupBySubcategory={groupBySubcategory}
      >
        <Box>
          <Box
            display='flex'
            alignItems='center'
            mb={2}
          >
            <Button onClick={handleBackClick}>Back</Button>
            <Typography
              variant='h6'
              sx={{ ml: 2 }}
            >
              {drilledDownCategory.name}
            </Typography>
          </Box>
          <AccountsGrid
            endDate={new Date().toISOString()}
            isWidget={true}
            filteredAccounts={drilledDownCategory.accounts}
          />
        </Box>
      </DrilldownView>
    );
  }

  return (
    <div
      id='category-bar-chart-print'
      ref={chartAreaRef}
    >
      <style>
        {`
@media print {
  /* Hide everything by default */
  body * { visibility: hidden !important; }

  /* Only show this component */
  #category-bar-chart-print, #category-bar-chart-print * { visibility: visible !important; }
  #category-bar-chart-print { position: absolute !important; top: 0 !important; left: 0 !important; right: 0 !important; width: 100% !important; margin: 0 !important; padding: 0 !important; }

  /* Stack charts vertically and full width when printing */
  #category-bar-chart-print .balance-chart-layout { display: flex !important; flex-direction: column !important; justify-content: flex-start !important; align-items: stretch !important; gap: 32px !important; }

  /* Hide UI controls in print */
  #category-bar-chart-print .print-hide { display: none !important; }

  /* One section per printed page */
  #category-bar-chart-print .print-section { page-break-after: always !important; break-after: page !important; page-break-inside: avoid !important; break-inside: avoid !important; width: 100% !important; min-height: auto !important; display: flex !important; flex-direction: column !important; justify-content: flex-start !important; align-items: stretch !important; margin: 0 !important; padding: 0 !important; }
  #category-bar-chart-print .print-section > * { page-break-inside: avoid !important; break-inside: avoid !important; }
  #category-bar-chart-print .print-section:last-child { page-break-after: auto !important; }
}
        `}
      </style>
      {enableDrilldown ? (
        <DrilldownView
          category={drilledDownCategory}
          fillHeight={fillDrilldownHeight}
          onBack={handleBackClick}
        >
          <BalanceChartLayout
            assetsTotal={assets.total}
            liabilitiesTotal={liabilities.total}
            assetsChart={AssetsBarChart}
            liabilitiesChart={LiabilitiesBarChart}
            forceColumnLayout={stackCharts}
          />
        </DrilldownView>
      ) : (
        <BalanceChartLayout
          assetsTotal={assets.total}
          liabilitiesTotal={liabilities.total}
          assetsChart={AssetsBarChart}
          liabilitiesChart={LiabilitiesBarChart}
          forceColumnLayout={stackCharts}
        />
      )}
    </div>
  );
}
